<template>
  <div v-if="show">
    <div
      class="tooltip relative empty:hidden text-xs leading-[1.125rem] border w-full z-99 rounded-[8px] max-w-fit"
      :class="[
        appendPopoverClass,
        component.main,
        {
          'mb-[0.375rem]': !tipInSide && tipY === 'bottom',
          'mt-[0.375rem]': !tipInSide && tipY === 'top',
          'ml-[0.375rem]': tipInSide && tipX === 'left',
          'mr-[0.375rem]': tipInSide && tipX === 'right',
        },
      ]"
    >
      <template v-if="component.icon && hasIcon">
        <IconsInfoCircle
          :width="component.icon.width"
          :height="component.icon.height"
          type="outline"
          fill="#fff"
        />
      </template>

      <p
        v-if="$slots.title"
        class="tooltip__title text-sm empty:hidden"
        :class="{
          'ml-2': component.icon.icon && size === 'large',
          'text-sm/[150%]': size === 'base',
          'font-bold': type === 'info',
        }"
      >
        <slot name="title" />
      </p>

      <p
        v-if="$slots.description && type != 'error'"
        class="tooltip__description text-sm empty:hidden"
        :class="{
          'ml-2': component.icon.icon && size === 'large',
          'text-xs': size === 'base',
        }"
      >
        <slot name="description" />
      </p>

      <slot v-if="$slots.default" />

      <div
        ref="tooltip-arrow"
        class="tooltip-arrow"
        :class="component.tipPosition"
      >
        <V2AtomsTooltipArrow
          :fill="component.tipColors.bg"
          :fill-border="component.tipColors.border"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
defineOptions({
  name: 'V2AtomsTooltip'
})

defineEmits(['close'])

const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error'].includes(value)
  },

  size: {
    type: String,
    default: 'base',
    validator: value => ['base', 'large'].includes(value)
  },

  tipX: {
    type: String,
    default: 'center',
    validator: value => ['left', 'center', 'right'].includes(value)
  },

  tipXAdjustment: {
    type: String,
    default: ''
  },

  tipXRightClass: {
    type: String,
    default: ''
  },

  tipY: {
    type: String,
    default: 'bottom',
    validator: value => ['top', 'bottom', 'middle'].includes(value)
  },

  tipYAdjustment: {
    type: String,
    default: ''
  },

  tipInSide: {
    type: Boolean,
    default: false
  },

  hasIcon: {
    type: Boolean,
    default: true
  },

  appendPopoverClass: {
    type: String,
    default: ''
  }
})

const show = defineModel({
  type: Boolean,
  default: false
})

const component = computed(() => {
  const icon = getKey(props.type, {
    error: {
      icon: 'info',
      width: 20,
      height: 20
    },
    info: false
  })

  const color = getKey(props.type, {
    info: {
      bg: 'bg-semantic-surface-container-lowest',
      border: 'border-gray-400'
    },
    error: {
      bg: 'bg-semantic-states-error-default',
      border: 'bg-semantic-states-error-default'
    }
  })

  const main = (() => {
    const size = getKey(props.size, {
      large: 'p-2',
      base: 'p-1 px-2'
    })

    let map = getKey(props.type, {
      info: 'text-semantic-surface-on-surface !drop-shadow-sm-3',
      error: 'text-semantic-states-error-on-default !drop-shadow-sm-2'
    })

    if (icon && props.hasIcon) {
      map += ` inline-grid grid-cols-[1rem,auto] items-center ${
        props.type === 'error' ? 'gap-1' : 'gap-2'
      }`
    } else {
      map += ' inline-block align-top'
    }

    map += ` ${color.bg} ${color.border}`

    return `${map} ${size}`
  })()

  const tipXAdjustment = Number(`${props.tipXAdjustment}`.replace(/px/, ''))

  const tipPosition = (() => {
    const map = {
      top: '-top-2 rotate-180',
      right: 'right-1',
      bottom: '-bottom-[0.4375rem]',
      left: 'left-1',
      center: '-translate-x-1/2 left-1/2',
      middle: 'translate-y-[-50%] top-1/2'
    }

    if (props.tipInSide) {
      map.left = '-left-[15px] rotate-90'
      map.right = '-right-[15px] -rotate-90'
      map.bottom = 'bottom-[0.875rem]'
      map.top = 'top-[0.875rem]'
    }

    if (props.tipXRightClass && !props.tipInSide) {
      map.right = props.tipXRightClass
    }

    const tip = props.$refs?.['tooltip-arrow']
    if (tip) {
      if (['bottom', 'top'].includes(props.tipY)) {
        if (tipXAdjustment > 0) {
          tip.style.left = `${tipXAdjustment}px`
          tip.style.transform = `
                translateX(0)
                rotate(${props.tipY === 'top' ? '180deg' : '0'})
              `
        } else {
          tip.style.left = '50%'
          tip.style.transform = `
                translateX(-50%)
                rotate(${props.tipY === 'top' ? '180deg' : '0'})`
        }
      }
    }

    return `absolute ${map[props.tipX]} ${map[props.tipY]}`
  })()

  const tipColors = getKey(props.type, {
    info: {
      bg: '#fbf7ff',
      border: '#d2d2d2'
    },
    error: {
      bg: '#A3000B',
      border: '#ffffff'
    }
  })

  if (props.type !== 'info' && props.size === 'base') {
    icon.height = 14
    icon.width = 14
  }

  return {
    icon,
    color,
    main,
    tipPosition,
    tipColors
  }
})
</script>
